import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  clampColumns,
  clampTableHeight,
  DEFAULT_KPI_COLUMNS,
  DEFAULT_TABLE_COLUMNS,
  DEFAULT_TABLE_HEIGHT,
  densityClassForColumns,
  loadAllPageLayouts,
  MAX_COLUMNS,
  MAX_TABLE_HEIGHT,
  MIN_COLUMNS,
  MIN_TABLE_HEIGHT,
  pageFromStorageKey,
  saveKpiColumns,
  saveTableColumns,
  saveTableHeight,
  TABLE_HEIGHT_STEP,
} from '../utils/cardGridLayout';

const CardGridLayoutContext = createContext(null);

function resolvePage(page, activePage) {
  return page || activePage || 'wh';
}

function readLayout(layoutsByPage, page) {
  const layout = layoutsByPage[page];
  return {
    kpiColumns: layout?.kpiColumns ?? DEFAULT_KPI_COLUMNS,
    tableColumns: layout?.tableColumns ?? DEFAULT_TABLE_COLUMNS,
    tableHeight: layout?.tableHeight ?? DEFAULT_TABLE_HEIGHT,
  };
}

export function CardGridLayoutProvider({ children, activePage = 'wh' }) {
  const [layoutsByPage, setLayoutsByPage] = useState(() => loadAllPageLayouts());

  const setKpiColumnsForPage = useCallback((page, next) => {
    const targetPage = page || 'wh';
    setLayoutsByPage((prev) => {
      const current = readLayout(prev, targetPage);
      const value = clampColumns(
        typeof next === 'function' ? next(current.kpiColumns) : next,
        DEFAULT_KPI_COLUMNS
      );
      saveKpiColumns(targetPage, value);
      return {
        ...prev,
        [targetPage]: {
          ...current,
          kpiColumns: value,
        },
      };
    });
  }, []);

  const setTableColumnsForPage = useCallback((page, next) => {
    const targetPage = page || 'wh';
    setLayoutsByPage((prev) => {
      const current = readLayout(prev, targetPage);
      const value = clampColumns(
        typeof next === 'function' ? next(current.tableColumns) : next,
        DEFAULT_TABLE_COLUMNS
      );
      saveTableColumns(targetPage, value);
      return {
        ...prev,
        [targetPage]: {
          ...current,
          tableColumns: value,
        },
      };
    });
  }, []);

  const setTableHeightForPage = useCallback((page, next) => {
    const targetPage = page || 'wh';
    setLayoutsByPage((prev) => {
      const current = readLayout(prev, targetPage);
      const value = clampTableHeight(
        typeof next === 'function' ? next(current.tableHeight) : next,
        DEFAULT_TABLE_HEIGHT
      );
      saveTableHeight(targetPage, value);
      return {
        ...prev,
        [targetPage]: {
          ...current,
          tableHeight: value,
        },
      };
    });
  }, []);

  const setKpiColumns = useCallback(
    (next) => setKpiColumnsForPage(activePage, next),
    [activePage, setKpiColumnsForPage]
  );

  const setTableColumns = useCallback(
    (next) => setTableColumnsForPage(activePage, next),
    [activePage, setTableColumnsForPage]
  );

  const setTableHeight = useCallback(
    (next) => setTableHeightForPage(activePage, next),
    [activePage, setTableHeightForPage]
  );

  const getColumns = useCallback(
    (section, page) => {
      const layout = readLayout(layoutsByPage, resolvePage(page, activePage));
      return section === 'tables' ? layout.tableColumns : layout.kpiColumns;
    },
    [layoutsByPage, activePage]
  );

  const getTableHeight = useCallback(
    (page) => readLayout(layoutsByPage, resolvePage(page, activePage)).tableHeight,
    [layoutsByPage, activePage]
  );

  const getGridStyle = useCallback(
    (section, page) => {
      const targetPage = resolvePage(page, activePage);
      const columns = getColumns(section, targetPage);
      const style = {};
      if (section === 'kpi') {
        style['--kpi-columns'] = String(columns);
      } else {
        style.gridTemplateColumns = `repeat(${columns}, minmax(0, 1fr))`;
        style['--dashboard-table-card-height'] = `${getTableHeight(targetPage)}px`;
      }
      return style;
    },
    [activePage, getColumns, getTableHeight]
  );

  const getDensityClass = useCallback(
    (section, page) => densityClassForColumns(getColumns(section, page)),
    [getColumns]
  );

  const activeLayout = readLayout(layoutsByPage, activePage);

  const value = useMemo(
    () => ({
      activePage,
      kpiColumns: activeLayout.kpiColumns,
      tableColumns: activeLayout.tableColumns,
      tableHeight: activeLayout.tableHeight,
      setKpiColumns,
      setTableColumns,
      setTableHeight,
      setKpiColumnsForPage,
      setTableColumnsForPage,
      setTableHeightForPage,
      getColumns,
      getTableHeight,
      getGridStyle,
      getDensityClass,
      pageFromStorageKey,
      minColumns: MIN_COLUMNS,
      maxColumns: MAX_COLUMNS,
      minTableHeight: MIN_TABLE_HEIGHT,
      maxTableHeight: MAX_TABLE_HEIGHT,
      tableHeightStep: TABLE_HEIGHT_STEP,
    }),
    [
      activePage,
      activeLayout.kpiColumns,
      activeLayout.tableColumns,
      activeLayout.tableHeight,
      setKpiColumns,
      setTableColumns,
      setTableHeight,
      setKpiColumnsForPage,
      setTableColumnsForPage,
      setTableHeightForPage,
      getColumns,
      getTableHeight,
      getGridStyle,
      getDensityClass,
    ]
  );

  return (
    <CardGridLayoutContext.Provider value={value}>{children}</CardGridLayoutContext.Provider>
  );
}

export function useCardGridLayout(page) {
  const ctx = useContext(CardGridLayoutContext);
  if (!ctx) {
    throw new Error('useCardGridLayout must be used within CardGridLayoutProvider');
  }

  if (!page) return ctx;

  return {
    ...ctx,
    kpiColumns: ctx.getColumns('kpi', page),
    tableColumns: ctx.getColumns('tables', page),
    tableHeight: ctx.getTableHeight(page),
    getGridStyle: (section) => ctx.getGridStyle(section, page),
    getDensityClass: (section) => ctx.getDensityClass(section, page),
  };
}
